import React from 'react'
import { useState, useEffect, useMemo } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faPlus, faUpload, faSpinner, faSearch, faArrowRotateLeft, faWarning, faExclamationTriangle, faTable, faList
} from '@fortawesome/free-solid-svg-icons'
import SideBar from '../../SideBar'
import { useTheme } from '../../../context/ThemeProvider'
import StaffGroup from './StaffGroup'
import CreateStaffSlideOut from './Components/CreateStaffSlideOut'
import UploadStaffSlideOut from './Components/UploadStaffSlideOut'
import useAxiosPrivateSAI from '../../../hooks/useAxiosPrivateSAI'
import {toast} from 'sonner'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import { Tooltip } from 'react-tooltip'

const StaffDetails = () => {
  const [activeView, setActiveView] = useState('form')
  const [open, setOpen] = useState(false)
  const [openCsv, setOpenCsv] = useState(false)
  const [mode, setMode] = useState('create')
  const [selectedStaff, setSelectedStaff] = useState(null)
  const axios = useAxiosPrivateSAI()
  const theme = useTheme()
  const [isLoading, setIsLoading] = useState(false)
  const [policyData, setPolicyData] = useState({})
  const [activeTab, setActiveTab] = useState('live')
  const [archivedGroupedStaff, setArchivedGroupedStaff] = useState({})
  const [searchTerm, setSearchTerm] = useState('')
  const navigate = useNavigate()
  const [showConfirm, setShowConfirm] = useState(false)
  const [pendingHref, setPendingHref] = useState(null)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [orgType, setOrgType] = useState(null)
  const [canSubmit, setCanSubmit] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [viewMode, setViewMode] = useState("grid")
  const [selectedStaffIds, setSelectedStaffIds] = useState([])
  const [bulkMode, setBulkMode] = useState(false);

  const style = {
    backgroundColor: theme.backgroundColor,
    color: theme.textColor,
  }

  const [groupedStaff, setGroupedStaff] = useState({})

  const handleStaffUpdate = (group, updatedStaff) => {
    setGroupedStaff(prev => ({
      ...prev,
      [group]: prev[group].map(member =>
        member.id === updatedStaff.id ? updatedStaff : member
      )
    }))
  }
  const handleAddStaff = () => {
    setSelectedStaff(null)
    setMode('create')
    setOpen(true)
  }

  const [isArchivedContext, setIsArchivedContext] = useState(false)

  const handleEditStaff = (staff, fromArchived = false) => {
    setSelectedStaff(staff)
    setMode('edit')
    setIsArchivedContext(fromArchived)
    setOpen(true)
  }

  const handleUploadCsv = () => {
    setOpenCsv(true)
  }

  const fetchOffPolicyStaff = async (policyId) => {
    try {
      const orgId = JSON.parse(localStorage.getItem('orgType'))?.id
      if (!orgId || !policyId) return

      const response = await axios.get(`/api/policys/staff_off_policy/${policyId}`)
      const staffs = response.data?.staffs ?? []
      const grouped = staffs.reduce((acc, member) => {
        const title = (member.staff_type || 'Other').trim()
        if (!acc[title]) acc[title] = []
        const [forename, ...rest] = (member.name || '').split(' ')
        acc[title].push({
          id: member.staff_id,
          forename: forename || '',
          surname: rest.join(' ') || '',
          dob: member.date_of_birth || '',
          inception: member.inception_date || '',
          expiry: member.end_date || '',
          fte: member.fte || '',
          staff_type: member.staff_type || '',
          active: member.active,
          policy_staff_status: member.policy_staff_status,
          isSubmitted: member.isSubmitted,
          policy_staff_id: member.policy_staff_id,
          is_renewal: member.is_renewal,
          paid: member.paid,
          personal_days: member.personal_days,
          sickness_days: member.sickness_days,
          staff_changes_inception_date: member.staff_changes_inception_date,
          changes: member.changes,
          created_at: member.created_at,
          policy_id: member.policy_id,
        })
        return acc
      }, {})

      setArchivedGroupedStaff(grouped)
      toast.success('Successfully retrieved archived staff')
    } catch (err) {
      toast.error('Failed to fetch archived staff')
      // console.error(err)
    } 
  }

  const fetchStaff = async () => {
    try {
      setIsLoading(true)
      const orgId = JSON.parse(localStorage.getItem('orgType'))?.id;
      if (!orgId) return;
  
      const response = await axios.get(`/api/policys/org_policys/academy/${orgId}?area=staffs`);
      const groupList = response.data[0]?.groupList || [];
  
      const formattedStaff = groupList.reduce((acc, group) => {
        acc[group.title.trim()] = group.staff.map(member => ({
          id: member.staff_id,
          forename: member.name?.split(' ')[0] || '',
          surname: member.name?.split(' ').slice(1).join(' ') || '',
          dob: member.date_of_birth || '',
          inception: member.inception_date || '',
          expiry: member.end_date || '',
          fte: member.fte || '',
          staff_type: member.staff_type || '',
          policy_staff_id: member.policy_staff_id,
          policy_staff_status: member.policy_staff_status,
          has_open_claim: member.staff?.has_open_claim ?? member.staff?.has_open_claims ?? false,
          // active: member.active, <-- seems to cause issues when adding back in
          isSubmitted: member.isSubmitted,
          is_renewal: member.is_renewal,
          paid: member.paid,
          personal_days: member.personal_days,
          sickness_days: member.sickness_days,
          staff_changes_inception_date: member.staff_changes_inception_date,
          changes: member.changes,
          created_at: member.created_at,
          policy_id: member.policy_id,
        }))
        return acc
      }, {})
  
      toast.success('Successfully retrieved staff list');
      setGroupedStaff(formattedStaff);
    } catch (err) {
      toast.error('Failed to fetch staff list:', err);
    }
    setIsLoading(false)
  }
  
  const handleStaffDeleted = (deletedStaffId) => {
    setGroupedStaff(prev => {
      const next = { ...prev };
      for (const [group, members] of Object.entries(next)) {
        next[group] = members.map(m =>
          m.id === deletedStaffId
            ? {
                ...m,
                expiry: moment().format('YYYY-MM-DD'),
                active: 0,
                policy_staff_status: 'draft',
                isSubmitted: 0,
                deletedFlag: true,
              }
            : m
        )
      }
      return next
    })

    setArchivedGroupedStaff(prev => {
      const next = {}
      Object.entries(prev).forEach(([group, members]) => {
        next[group] = members.filter(m => m.id !== deletedStaffId)
      })
      return next
    })
  }
  
  useEffect(() => {
    setPolicyData(JSON.parse(localStorage.getItem('policyData')))
    setOrgType(JSON.parse(localStorage.getItem('orgType')))
    fetchStaff()
  }, [])

  useEffect(() => {
    if (policyData?.policy_id) fetchOffPolicyStaff(policyData?.policy_id)
  }, [policyData])


  // const isExpired = (expiry) => {
  //   const d = moment(expiry, ['YYYY-MM-DD', 'DD/MM/YYYY'], true)
  //   if (!d.isValid()) return false
  //   const today = moment().startOf('day')
  //   return d.isBefore(today)
  // }

  const isExpired = (expiry) => {
    if (!expiry) return false
    const d = moment(expiry, ['YYYY-MM-DD', 'DD/MM/YYYY'], true)
    return d.isValid() && !d.isAfter(moment().startOf('day'))
  }
  
  const isForceLive = (m) => m.has_open_claim === true
  const isSoftDeleted = (m) => (m.active === 0 || m.deletedFlag === true) && !isForceLive(m)
  const isArchivedFromLive = (m) => !isForceLive(m) && (isExpired(m.expiry) || isSoftDeleted(m))

  const matchesSearch = (memeber, term) => {
    if (!term) return true
    const query = term.trim().toLowerCase()
    const full = `${memeber.forename ?? ''} ${memeber.surname ?? ''}`.trim().toLowerCase()
    const raw = (memeber.name ?? '').toLowerCase()
    return full.includes(query) || raw.includes(query)
  } 

  const filteredGroupedStaff = useMemo(() => {
    const query = searchTerm
    return Object.fromEntries(
      Object.entries(groupedStaff)
        .map(([group, members]) => [
          group,
          members.filter(staff => {
            const inTab = activeTab === 'live'
              ? (isForceLive(staff) || (!isExpired(staff.expiry) && !isSoftDeleted(staff)))
              : (!isForceLive(staff) && (isExpired(staff.expiry) || isSoftDeleted(staff)))
            return inTab && matchesSearch(staff, query);
          })
        ])
        .filter(([, members]) => members.length > 0)
    )
  }, [groupedStaff, activeTab, searchTerm])  

  const mergedArchivedGroupedStaff = useMemo(() => {
    const result = {}
  
    for (const [group, members] of Object.entries(archivedGroupedStaff)) {
      result[group] = [...members]
    }
  
    // add on-policy items that are archived (expired/soft-deleted) but not force-live
    for (const [group, members] of Object.entries(groupedStaff)) {
      const toAdd = members.filter(isArchivedFromLive)
      if (!toAdd.length) continue
  
      if (!result[group]) result[group] = [];
      const seen = new Set(result[group].map(m => m.id))
      toAdd.forEach(m => {
        if (!seen.has(m.id)) result[group].push(m)
      });
    }
  
    return result;
  }, [groupedStaff, archivedGroupedStaff])
  
  const filteredArchivedGroupedStaff = useMemo(() => {
    const query = searchTerm;
    return Object.fromEntries(
      Object.entries(mergedArchivedGroupedStaff)
        .map(([group, members]) => [
          group,
          members.filter(member => matchesSearch(member, query))
        ])
        .filter(([, members]) => members.length > 0)
    )
  }, [mergedArchivedGroupedStaff, searchTerm])

  const liveCount = Object.values(groupedStaff)
  .flat()
  .filter(m => isForceLive(m) || (!isExpired(m.expiry) && !isSoftDeleted(m))).length

  const expiredCount = Object.values(groupedStaff)
    .flat()
    .filter(m => !isForceLive(m) && (isExpired(m.expiry) || isSoftDeleted(m))).length

  const archivedCount = Object.values(mergedArchivedGroupedStaff).flat().length

  const hasDrafts = Object.values(groupedStaff)
    .flat()
    .some(m => m.policy_staff_status === 'draft');

  const {
    hasDraftsLive,
    hasDraftsExpired,
    hasDraftsArchived,
    hasDraftsAny,
  } = useMemo(() => {
    const allLive = Object.values(groupedStaff).flat()
    const allArchived = Object.values(archivedGroupedStaff).flat()

    const expiredFromLive = allLive.filter(m => isExpired(m.expiry) || isSoftDeleted(m))

    const hasDraftsLive = allLive.some(m => m.policy_staff_status === 'draft')
    const hasDraftsExpired = expiredFromLive.some(m => m.policy_staff_status === 'draft')
    const hasDraftsArchived = allArchived.some(m => m.policy_staff_status === 'draft')

    return {
      hasDraftsLive,
      hasDraftsExpired,
      hasDraftsArchived,
      hasDraftsAny: hasDraftsLive || hasDraftsExpired,
    };
  }, [groupedStaff, archivedGroupedStaff])

  const hasUnsavedChanges = hasDraftsAny
  
  const attemptNavigate = (href) => {
    if (hasUnsavedChanges) {
      setPendingHref(href)
      setShowConfirm(true)
    } else {
      if (href) {
        navigate(href);
      }
    }
  }
  
  const confirmLeave = () => {
    setShowConfirm(false)
    if (pendingHref) navigate(pendingHref)
    setPendingHref(null)
  }
  
  const cancelLeave = () => {
    setShowConfirm(false);
    setPendingHref(null);
  }
  
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!hasUnsavedChanges) return
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasUnsavedChanges])

  const revertAllStaffChanges = async () => {
    const policyId = policyData?.policy_id;
    if (!policyId) {
      toast.error('Missing policy ID');
      return;
    }

    try {
      setIsLoading(true)
      await axios.post(`/api/policy_staffs/revert/${policyId}`)
      toast.success('Changes reverted successfully')

      await Promise.all([fetchStaff(), fetchOffPolicyStaff(policyId)])
    } catch (err) {
      console.error('Revert error:', err)
      toast.error('Failed to revert changes')
    } finally {
      setIsLoading(false)
    }
  }

  const removeFromArchived = (staffId, staffType) => {
    setArchivedGroupedStaff(prev => {
      const group = (staffType || '').trim()
      if (!prev[group]) return prev
      const next = { ...prev }
      next[group] = next[group].filter(m => m.id !== staffId)
      if (next[group].length === 0) delete next[group]
      return next
    })
  }

  const addUploadedRowsToGroups = (rows) => {
    const byGroup = {};

    rows.forEach(r => {
      const groupTitle = (r.staff_type || 'Other').trim();
      const tempId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2)}`;

      const mapped = {
        id: tempId,
        forename: r.forename ?? '',
        surname:  r.surname ?? '',
        dob:      r.date_of_birth ?? '',
        inception: r.inception_date || moment().format('YYYY-MM-DD'),
        expiry:    r.end_date || '',

        fte:        r.fte ?? '',
        staff_type: groupTitle,
        policy_staff_status: 'draft',
        isSubmitted: 0,
        active: 0,
      }

      if (!byGroup[groupTitle]) byGroup[groupTitle] = [];
      byGroup[groupTitle].push(mapped)
    })

    setGroupedStaff(prev => {
      const next = { ...prev }
      Object.entries(byGroup).forEach(([group, items]) => {
        next[group] = [...(next[group] ?? []), ...items]
      })
      return next
    })

    fetchStaff()
  }

  const getTotalFte = () => {
    const totalFte = []
    let zeroFteCount = 0
  
    Object.entries(groupedStaff).forEach(([groupTitle, staffList]) => {
      let groupFte = 0
  
      staffList.forEach(staff => {
        const fte = parseFloat(staff.fte) || 0
        groupFte += fte
        if (fte === 0) {
          zeroFteCount++
        }
      })
  
      totalFte.push({
        title: groupTitle,
        fte: groupFte,
      })
    })
  
    return { totalFte, zeroFteCount }
  }  

  const handleBulkExpire = async (ids) => {
    try {
      if (selectedStaffIds.length === 0) {
        toast.error("No staff selected")
        return
      }
  
      await axios.post("/api/policy_staffs/bulk-expire", {
        policy_staff_ids: selectedStaffIds,
      })
  
      toast.success("Selected staff expired successfully")
      setSelectedStaffIds([])
      fetchStaff()
    } catch (err) {
      toast.error("Failed to expire staff")
      console.error(err)
    }
  }


  const submitPolicyStaff = async () => {
    if (!orgType?.id || !orgType?.type) {
      toast.error('Organisation info missing');
      return;
    }

    try {
      setIsSubmitting(true)
  
      const groupList = []
      const totalFte = []
      let zeroFteCount = 0
  
      Object.entries(groupedStaff).forEach(([title, members]) => {
        let fte = 0
        const staff = members.map((m) => {
          const f = parseFloat(m.fte) || 0
          fte += f
          if (f === 0) zeroFteCount++
          return m
        })
  
        groupList.push({
          title: title.trim(),
          fte,
          policy_id: policyData.policy_id,
          staff,
        })
  
        totalFte.push({
          title: title.trim(),
          fte,
        })
      })
  
      if (zeroFteCount > 0) {
        toast.warning(`${zeroFteCount} staff member(s) have 0.00 FTE`)
      }
  
      const payload = {
        group: groupList,
        all: [
          {
            organization_id: policyData.policyable_id,
            organization_type: orgType.type,
            organization_name: orgType.name ?? 'Unknown School',
            groupList,
            offerPopup: 0,
            policy_data: {
              policy_id: policyData.policy_id,
              schedule: policyData.schedule,
              policy_status: policyData.policy_status,
              policy_inception: policyData.policy_inception,
              policy_expire: policyData.policy_expire,
              department: policyData.department,
              product: policyData.product,
              ...policyData
            }
          }
        ],
        total_fte: totalFte,
      }
  
      const response = await axios.post('/api/submit/policy_staffs', payload)
  
      toast.success(response.data?.message || 'Staff submitted successfully')
      localStorage.removeItem('hasChanges')
  
      setTimeout(() => {
        window.location.reload()
      }, 1000)
    } catch (error) {
      console.error('Failed submitting staff', error)
      toast.error('Something went wrong submitting staff.')
    } finally {
      setIsSubmitting(false)
      setCanSubmit(false)
    }
  }
  
  return (
    <div className="w-full">
      {showConfirm && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-500/10">
                <FontAwesomeIcon icon={faWarning} className="size-6 text-amber-600 dark:text-amber-400" />
              </div>
            <h3 className="text-lg font-semibold text-center mt-4 mb-2">Unsaved changes</h3>
            <p className="mt-4 text-gray-600 text-center mb-8">
              You have unsaved changes. Navigating away will remove these changes.
            </p>
            <div className="pt-2 flex items-center justify-between">
              <button
                onClick={confirmLeave}
                className="inline-flex items-center border border-red-600 bg-red-600 hover:shadow-lg rounded-md overflow-hidden text-sm font-medium"
              >
                <span className="px-4 py-2 text-white">Leave Anyway</span>
              </button>
              <button
                onClick={cancelLeave}
                className="inline-flex items-center border border-sas bg-sas hover:shadow-lg rounded-md overflow-hidden text-sm font-medium"
              >
                <span className="px-4 py-2 text-white">Stay</span>
              </button>
            </div>
          </div>
        </div>
      )}
      {canSubmit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white max-w-md w-full rounded-lg p-6 shadow-lg">
            <div className="flex justify-center mb-4">
              <FontAwesomeIcon icon={faExclamationTriangle} className="h-12 w-12 text-yellow-400 rounded-full bg-yellow-50 p-4 border border-yellow-200" />
            </div>
            <h2 className="text-center text-xl font-semibold text-gray-800">Are you sure?</h2>
            <p className="mt-2 text-center text-gray-600 text-sm">
              Are you sure you want to submit these staff details for your school?
            </p>
            <div className="mt-6 flex justify-center items-center gap-4">
              <button
                onClick={() => setCanSubmit(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                onClick={submitPolicyStaff}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Yes, submit staff'}
              </button>
            </div>
          </div>
        </div>
      )}
      <>
        {showConfirmModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
              <h2 className="text-lg font-semibold text-gray-800">
                Revert all unsubmitted staff changes?
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                This will revert the changes you have made to all <strong>unsubmitted</strong> staff members. This action cannot be undone.
              </p>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100"
                  onClick={() => setShowConfirmModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700"
                  onClick={revertAllStaffChanges}
                >
                  Yes, revert
                </button>
              </div>
            </div>
          </div>
        )}
      </>
      <div className="mb-8">
        <h3 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white text-center pt-6">
          Manage your Staff Details Here
        </h3>
        <h4 className="text-lg font-medium text-white text-center">
          Welcome to your Staff Absence Portal. Here you can manage your staff details.
        </h4>
      </div>
      {/* Main Content Layout */}
      <div className="flex flex-col lg:flex-row max-w-[90rem] mx-auto lg:space-x-6 mt-32">
        {/* Sidebar Card */}
        <SideBar active={'Staff Details'}  onNavigate={attemptNavigate}/>
        {/* Main Card Section */}
        <main className="w-full lg:w-4/5 space-y-4">
          <div className="flex flex-wrap items-center justify-between mb-6 gap-4">
            <div className='flex gap-3'>
              <button
                onClick={() => setCanSubmit(true)}
                className={`inline-flex items-center border shadow-md hover:shadow-lg bg-white ${
                  activeView === 'form' ? 'border-sas text-sas' : 'border-white text-white'
                } rounded-md overflow-hidden text-sm font-medium`}
              >
                <span className="px-4 py-2">Submit Staff Details</span>
                <span className="bg-sas text-white px-3 py-2">
                  <FontAwesomeIcon icon={faPlus} />
                </span>
              </button>
              {(hasDraftsAny) && (
                <button
                  onClick={() => setShowConfirmModal(true)}
                  className={`inline-flex items-center border shadow-md hover:shadow-lg bg-white ${
                    activeView === 'form' ? 'border-orange-600 text-orange-600' : 'border-white text-white'
                  } rounded-md overflow-hidden text-sm font-medium`}
                >
                  <span className="px-4 py-2">Revert All Changes</span>
                  <span className="bg-orange-600 text-white px-3 py-2">
                    <FontAwesomeIcon icon={faArrowRotateLeft} />
                  </span>
                </button>
              )}
            </div>

            {/* Button Group */}
            <div className="flex gap-3">
              <button
                onClick={() => handleAddStaff()}
                className={`inline-flex items-center border ${
                  activeView === 'form' ? 'border-white text-white' : 'border-white text-white'
                } rounded-md overflow-hidden text-sm font-medium`}
              >
                <span className="px-4 py-2">Add Staff Member</span>
                <span className="bg-white text-sas px-3 py-2">
                  <FontAwesomeIcon icon={faPlus} />
                </span>
              </button>
              <>
                <Tooltip anchorSelect=".info" place="top">
                  Please submit your changes before uploading a xlsx
                </Tooltip>
                <span className={`${hasDraftsAny ? 'info' : ''}`}>
                  <button
                    onClick={() => handleUploadCsv()}
                    disabled={hasDraftsAny}
                    className={`inline-flex items-center border rounded-md overflow-hidden text-sm font-medium ${
                      hasDraftsAny
                        ? 'opacity-50 cursor-not-allowed border-gray-300 text-gray-300'
                        : 'border-white text-white'
                    }`}
                  >
                    <span className="px-4 py-2">Upload CSV</span>
                    <span className="bg-white text-sas px-3 py-2">
                      <FontAwesomeIcon icon={faUpload} />
                    </span>
                  </button>
                </span>
              </>
            </div>
          </div>
          <div>
            <div
              className={`inline-flex items-center border shadow-md  ${
                hasDraftsAny ? 'border-orange-600 text-orange-600 bg-orange-50' : 'border-green-600 text-green-600 bg-green-50'
              } rounded-md overflow-hidden text-sm font-medium`}
            >
              <span className="px-4 py-2">
                {hasDraftsAny
                  ? 'This staff list has unsaved changes. Please submit these else they will be lost'
                  : 'This staff list has been saved'}
              </span>
            </div>
          </div>
          <div className='flex justify-between items-center'>
            {/* Search Input */}
            <div className="relative w-full sm:w-72">
              <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search staff"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setViewMode("grid")}
                className={`px-3 py-1 rounded ${viewMode === "grid" ? "bg-sas text-white" : "bg-gray-100"}`}
              >
              <FontAwesomeIcon icon={faTable} className="mr-2" />
                Grid
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`px-3 py-1 rounded ${viewMode === "list" ? "bg-sas text-white" : "bg-gray-100"}`}
              >
              <FontAwesomeIcon icon={faList} className="mr-2" />
                List
              </button>
            </div>
          </div>
          {/* ---- Live / Expired Tabs ---- */}
          <div className="mt-2">
            <div className="inline-flex rounded-md shadow-sm overflow-hidden border bg-white">
              <button
                onClick={() => setActiveTab('live')}
                className={`px-4 py-2 flex items-center text-sm font-medium ${activeTab === 'live' ? 'bg-sas text-white' : 'text-sas'}`}
              >
                Live <span className="mx-1 opacity-80">({liveCount})</span> {hasDraftsLive === true  && <div className={`text-xs ${activeTab === 'live' ? ' text-orange-100' : 'text-orange-600'}`} aria-label="Has drafts" >(Unsaved Changes) </div> }
              </button>
              <button
                onClick={() => setActiveTab('archived')}
                className={`px-4 py-2 text-sm flex items-center font-medium border-l ${activeTab === 'archived' ? 'bg-sas text-white' : 'text-sas'}`}
              >
                Expired <span className="mx-1 opacity-80">({archivedCount})</span> 
              </button>
            </div>
          </div>

          {isLoading ? (
            <div className="flex flex-col justify-start items-center h-full text-sas">
              <div>
                <FontAwesomeIcon icon={faSpinner} spin size="2x" />
              </div>
              <div className='text-sas mt-4'>Loading Staff List...</div>
            </div>
          ) : (
            <div className="space-y-4">
              {activeTab === 'archived'
                ? Object.entries(filteredArchivedGroupedStaff).map(([group, members]) => (
                    <StaffGroup
                      key={`arch-${group}`}
                      title={`${group} (Expired)`}
                      staff={members}
                      onEdit={(s) => handleEditStaff(s, true)} 
                      isArchived={true}
                      onUpdate={(updated) => {
                        setArchivedGroupedStaff(prev => ({
                          ...prev,
                          [group]: prev[group].map(m => (m.id === updated.id ? updated : m))
                        }))
                      }}
                      defaultOpen={false}
                    />
                  ))
                  : viewMode === "list" ? (
                    // --- LIVE STAFF: LIST VIEW ---
                  <div>
                    {/* Bulk toolbar */}
                    {viewMode === "list" && (
                      <div className="flex justify-between items-center my-4">
                        {!bulkMode ? (
                          <button
                            className="px-4 py-2 bg-red-600 text-white rounded"
                            onClick={() => setBulkMode(true)}
                          >
                            Bulk Expire
                          </button>
                        ) : (
                          <div className="flex gap-2">
                            <button
                              className="px-4 py-2 bg-gray-300 rounded"
                              onClick={() => {
                                setBulkMode(false);
                                setSelectedStaffIds([]);
                              }}
                            >
                              Cancel
                            </button>
                            <button
                              className="px-4 py-2 bg-red-600 text-white rounded"
                              disabled={selectedStaffIds.length === 0}
                              onClick={() => handleBulkExpire(selectedStaffIds)}
                            >
                              Expire Selected ({selectedStaffIds.length})
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {Object.entries(filteredGroupedStaff).map(([group, members]) => (
                      <div key={group} className="bg-white shadow rounded p-4 mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold">{group}</h4>
                        </div>

                        <table className="w-full text-sm text-left text-gray-800 border border-gray-300 rounded-lg overflow-hidden">
                          <thead className="bg-sas text-white">
                            <tr className="text-sm text-left">
                              {bulkMode && (
                                <th className="p-2">
                                  <input
                                    type="checkbox"
                                    checked={members.every((m) => selectedStaffIds.includes(m.policy_staff_id))}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedStaffIds((prev) => [
                                          ...new Set([...prev, ...members.map((m) => m.policy_staff_id)]),
                                        ]);
                                      } else {
                                        setSelectedStaffIds((prev) =>
                                          prev.filter((id) => !members.some((m) => m.policy_staff_id))
                                        );
                                      }
                                    }}
                                  />
                                  <span className="pl-2">Select</span>
                                </th>
                              )}
                              <th className="p-2">Name</th>
                              <th className="p-2">FTE</th>
                              <th className="p-2">Date of birth</th>
                              <th className="p-2">Inception</th>
                              <th className="p-2">Expiry</th>
                              {!bulkMode && <th className="p-2">Actions</th>}
                            </tr>
                          </thead>
                          <tbody>
                            {members.map((m) => (
                              <tr
                                key={m.id}
                                className="border-t odd:bg-blue-50 even:bg-white"
                              >
                                {bulkMode && (
                                  <td className="p-2">
                                    <input
                                      type="checkbox"
                                      checked={selectedStaffIds.includes(m.policy_staff_id)}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setSelectedStaffIds((prev) => [...prev, m.policy_staff_id]);
                                        } else {
                                          setSelectedStaffIds((prev) =>
                                            prev.filter((id) => id !== m.policy_staff_id)
                                          );
                                        }
                                      }}
                                    />
                                  </td>
                                )}
                                <td className="p-2">
                                  <div className="flex items-center gap-2">
                                    {m.policy_staff_status === 'draft' && (
                                      <span
                                        className="w-2 h-2 rounded-full bg-orange-500 inline-block"
                                        title="Unsaved changes"
                                      />
                                    )}
                                    <span>{m.forename} {m.surname}</span>
                                  </div>
                                </td>
                                <td className="p-2">{m.fte}</td>
                                <td className="p-2">{moment(m.dob).format("DD/MM/YYYY")}</td>
                                <td className="p-2">{moment(m.inception).format("DD/MM/YYYY")}</td>
                                <td className="p-2">{moment(m.expiry).format("DD/MM/YYYY")}</td>
                                {!bulkMode && (
                                  <td className="p-2">
                                    <button
                                      className="text-sas hover:text-sas-dark"
                                      onClick={() => handleEditStaff(m)}
                                    >
                                      Edit
                                    </button>
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                ) : (
                  // --- LIVE STAFF: GRID VIEW ---
                  Object.entries(filteredGroupedStaff).map(([group, members]) => (
                    <StaffGroup
                      key={group}
                      title={group}
                      staff={members}
                      onEdit={(s) => handleEditStaff(s, false)}
                      onUpdate={(updated) => handleStaffUpdate(group, updated)}
                      defaultOpen={false}
                    />
                  ))
                )}    
            </div>
          )}
        </main>
      </div>
      <CreateStaffSlideOut
        open={open}
        setOpen={setOpen}
        mode={mode}
        staff={selectedStaff}
        staffGroups={Object.keys(groupedStaff)}
        onStaffAddedOrUpdated={fetchStaff}
        onStaffDeleted={handleStaffDeleted}
        isArchivedContext={isArchivedContext} 
        onArchivedStaffRemoved={removeFromArchived}
      />
      <UploadStaffSlideOut
        openCsv={openCsv}
        setOpenCsv={setOpenCsv}
        policyData={policyData}
        onUploaded={addUploadedRowsToGroups}
      />
    </div>
  )
}

export default StaffDetails
